import {
  BodyText,
  Button,
  Callout,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  Skeleton,
} from "@/components/ui";
import { usePatientDemographics } from "@/features/Patient/PatientBar/network/usePatientDemographics";
import type { PatientDemographics } from "@/features/Patient/PatientBar/types/patientDemographics";
import { formatDate } from "@/lib/time";
import { useParams } from "@tanstack/react-router";
import { getMismatchedFields } from "./demographicsMismatch";
import type {
  PatientDemographicsEligibility,
  RxEligibilityResponse,
} from "./network/useRxEligibility";
import { useRxEligibility } from "./network/useRxEligibility";

interface EligibilityModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const STATUS_CLASSES: Record<string, string> = {
  covered: "text-success",
  "not-covered": "text-error",
  "out-of-pocket": "text-warning",
  unknown: "text-tertiary",
  "unable-to-process": "text-tertiary",
};

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="flex justify-between text-sm py-1.5 border-b last:border-0">
    <span className="font-medium text-tertiary">{label}</span>
    <span>{value}</span>
  </div>
);

const DemographicsSection = ({
  demographics,
  mismatchPayers,
  emrDemographics,
}: {
  demographics: PatientDemographicsEligibility;
  mismatchPayers: string[];
  emrDemographics: PatientDemographics | undefined;
}) => {
  const nameParts = [
    demographics.firstName,
    demographics.middleName,
    demographics.lastName,
    demographics.suffix,
  ].filter(Boolean);
  const fullName = nameParts.length > 0 ? nameParts.join(" ") : null;

  const { addressLine1, addressLine2, city, state, zip } =
    demographics.address ?? {};
  const cityStateZip = [city, state, zip].filter(Boolean).join(", ");

  const hasAddress = addressLine1 || addressLine2 || cityStateZip;

  if (
    !fullName &&
    !hasAddress &&
    !demographics.dateOfBirth &&
    !demographics.gender
  ) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      <BodyText size="sm" className="font-medium">
        Patient Demographics
      </BodyText>
      {mismatchPayers.length > 0 &&
        (() => {
          const mismatchedFields = getMismatchedFields(
            emrDemographics,
            demographics,
          );
          const description =
            mismatchedFields.length > 0
              ? `${mismatchedFields.join(", ")} differ from ${mismatchPayers.join(", ")}.`
              : `Demographics on file differ from those returned by: ${mismatchPayers.join(", ")}.`;
          return (
            <Callout
              variant="warning"
              title="Demographic mismatch"
              description={description}
            />
          );
        })()}
      <div className="rounded-md border overflow-hidden px-3">
        {fullName && <Row label="Name" value={fullName} />}
        {hasAddress && (
          <div className="flex justify-between text-sm py-1.5 border-b last:border-0">
            <span className="font-medium text-tertiary">Address</span>
            <div className="flex flex-col items-end">
              {(addressLine1 || addressLine2) && (
                <span>
                  {[addressLine1, addressLine2].filter(Boolean).join(" ")}
                </span>
              )}
              {cityStateZip && <span>{cityStateZip}</span>}
            </div>
          </div>
        )}
        {demographics.dateOfBirth && (
          <Row label="DOB" value={formatDate(demographics.dateOfBirth)} />
        )}
        {demographics.gender && (
          <Row label="Gender" value={demographics.gender} />
        )}
      </div>
    </div>
  );
};

const EligibilityContent = ({
  data,
  emrDemographics,
}: {
  data: RxEligibilityResponse;
  emrDemographics: PatientDemographics | undefined;
}) => {
  if (data.coverages.length === 0) {
    return (
      <BodyText size="sm" textColor="tertiary">
        No coverage information found.
      </BodyText>
    );
  }

  const mismatchPayers = data.coverages
    .filter((c) => c.demographicChanges)
    .map((c) => c.payerName);
  const demographics = (
    data.coverages.find((c) => c.demographicChanges && c.patientDemographics) ??
    data.coverages.find((c) => c.patientDemographics)
  )?.patientDemographics;
  return (
    <div className="flex flex-col gap-4">
      {demographics && (
        <DemographicsSection
          demographics={demographics}
          mismatchPayers={mismatchPayers}
          emrDemographics={emrDemographics}
        />
      )}
      <BodyText size="sm" className="font-medium">
        Payers
      </BodyText>
      {data.coverages.map((coverage) => (
        <div
          key={
            coverage.patientEligibilityId ??
            coverage.coverageId ??
            coverage.payerName
          }
          className="flex flex-col gap-3 border rounded-md p-3"
        >
          <div className="flex flex-col divide-y divide-gray-100 text-sm">
            {[
              ["Payer", coverage.payerName],
              ["Plan", coverage.planName],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between py-1.5">
                <span className="font-medium">{label}</span>
                <span>{value || "—"}</span>
              </div>
            ))}
          </div>

          {(coverage.pharmacyEligibilities?.length ?? 0) > 0 && (
            <div>
              <BodyText size="sm" className="font-medium">
                Coverage
              </BodyText>
              <ul className="mt-2 flex flex-col divide-y divide-gray-100">
                {coverage.pharmacyEligibilities?.map((pe, i) => (
                  <li
                    // biome-ignore lint/suspicious/noArrayIndexKey: no stable key available
                    key={i}
                    className="flex items-center justify-between text-sm py-1.5"
                  >
                    <span>{pe.name || pe.pharmacyType}</span>
                    <span
                      className={`text-left ${STATUS_CLASSES[pe.eligibilityStatus] ?? "text-tertiary"}`}
                    >
                      {pe.eligibilityStatus}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export const EligibilityModal = ({
  open,
  onOpenChange,
}: EligibilityModalProps) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { data, isLoading, isError, refetch } = useRxEligibility({
    patientId,
    enabled: open,
  });
  const { data: emrDemographics } = usePatientDemographics(patientId);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: loading skeleton
            <Skeleton key={i} className="h-8" />
          ))}
        </div>
      );
    }

    if (isError) {
      return (
        <div className="flex flex-col gap-3">
          <Callout
            variant="danger"
            title="Error loading eligibility"
            description="Unable to retrieve eligibility information."
          />
          <Button size="sm" variant="secondary" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      );
    }

    if (data) {
      return (
        <EligibilityContent data={data} emrDemographics={emrDemographics} />
      );
    }

    return null;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogOverlay />
      <DialogContent className="w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Eligibility</DialogTitle>
          <DialogDescription className="hidden">
            Patient insurance eligibility information
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto min-h-0">{renderContent()}</div>
      </DialogContent>
    </Dialog>
  );
};
